import { useQuery } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { BookingOpportunitiesPanel } from '@/components/BookingOpportunitiesPanel'

interface InquiryRow {
  id: string
  stage: string
  contactName: string | null
  contactOrg: string | null
  eventDate: string | null
  eventCity: string | null
  eventCountry: string | null
  venueName: string | null
  venueCapacity: number | null
  budgetMin: number | null
  budgetMax: number | null
  createdAt: string
  marketChannel: string | null
  eventTypeNormalized: string | null
}

interface PipelineResponse {
  items: InquiryRow[]
  total: number
}

const STAGE_COLOUR: Record<string, string> = {
  inquiry: 'bg-slate-100 text-slate-700',
  qualified: 'bg-blue-100 text-blue-700',
  offer_sent: 'bg-indigo-100 text-indigo-700',
  hold: 'bg-purple-100 text-purple-700',
  contract_issued: 'bg-amber-100 text-amber-700',
  contract_signed: 'bg-yellow-100 text-yellow-700',
  deposit_received: 'bg-lime-100 text-lime-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  advancing: 'bg-teal-100 text-teal-700',
  completed: 'bg-emerald-200 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-700',
  lost: 'bg-rose-100 text-rose-700',
}

export function PipelinePage() {
  const { data, error, isLoading } = useQuery<PipelineResponse, ApiClientError>({
    queryKey: ['pipeline'],
    queryFn: () => artistFetch<PipelineResponse>('/pipeline'),
  })

  return (
    <div>
      <PageHeader
        title="Pipeline"
        subtitle="Inquiry → confirmed show. Live from the API."
      />
      <div className="mb-6">
        <BookingOpportunitiesPanel compact />
      </div>
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data?.items ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No inquiries yet. Add one via the API or wait for the email triage."
          columns={[
            {
              key: 'stage',
              header: 'Stage',
              render: (r) => (
                <span
                  className={`inline-flex rounded px-2 py-0.5 text-xs ${
                    STAGE_COLOUR[r.stage] ?? 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {r.stage.replace(/_/g, ' ')}
                </span>
              ),
            },
            {
              key: 'channel',
              header: 'Channel',
              render: (r) =>
                r.marketChannel ? (
                  <span className="text-xs capitalize text-slate-600">
                    {r.marketChannel.replace(/_/g, ' ')}
                  </span>
                ) : (
                  '—'
                ),
            },
            {
              key: 'contact',
              header: 'Contact',
              render: (r) => (
                <div>
                  <div>{r.contactName ?? '—'}</div>
                  {r.contactOrg && (
                    <div className="text-xs text-ovation-muted">{r.contactOrg}</div>
                  )}
                </div>
              ),
            },
            {
              key: 'venue',
              header: 'Venue',
              render: (r) => r.venueName ?? '—',
            },
            {
              key: 'city',
              header: 'City',
              render: (r) =>
                r.eventCity
                  ? `${r.eventCity}${r.eventCountry ? `, ${r.eventCountry}` : ''}`
                  : '—',
            },
            {
              key: 'date',
              header: 'Date',
              render: (r) =>
                r.eventDate ? new Date(r.eventDate).toLocaleDateString() : '—',
            },
            {
              key: 'budget',
              header: 'Budget',
              align: 'right',
              render: (r) =>
                r.budgetMax != null
                  ? `£${(r.budgetMax / 100).toLocaleString()}`
                  : '—',
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}
